/**
 * Pure date helpers for the toolbar date picker (UTC-only, dependency-free).
 *
 * The status page reasons in UTC everywhere (DESIGN.md §11); the picker lets an
 * operator jump to a specific day's data via the uptime `anchor` param. The
 * backend's window ENDS at the anchor (`uptime.service.ts getWindow`), so
 * `anchorIsoForDay` resolves a picked day to its end-of-day instant (clamped to
 * now) — that makes the 24h window cover the chosen day and keeps the anchor's
 * UTC date equal to the picked day (no off-by-one in the pill label).
 *
 * No React/DOM — tested directly in `calendar.test.ts` (this repo runs vitest
 * with no jsdom).
 */

const DAY_MS = 86_400_000;

/** Monday-first weekday headers. */
export const WEEKDAY_LABELS = [
  'Mon',
  'Tue',
  'Wed',
  'Thu',
  'Fri',
  'Sat',
  'Sun',
] as const;

export interface DayCell {
  /** UTC day key, `YYYY-MM-DD`. */
  iso: string;
  /** Day-of-month, 1–31. */
  day: number;
  /** Whether the cell belongs to the displayed month (vs. a padding day). */
  inMonth: boolean;
}

export interface MonthView {
  year: number;
  /** 0-indexed month. */
  month: number;
  /** Human label, e.g. `May 2026`. */
  label: string;
  /** 6 weeks × 7 days, Monday-first. */
  weeks: DayCell[][];
}

export interface DayRange {
  minIso: string;
  maxIso: string;
}

/** Format an instant as a UTC `YYYY-MM-DD` key. */
export function utcDayKey(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Shift a (year, 0-indexed month) by `delta` months, wrapping the year. */
export function addMonths(
  year: number,
  month: number,
  delta: number,
): { year: number; month: number } {
  const total = year * 12 + month + delta;
  return { year: Math.floor(total / 12), month: ((total % 12) + 12) % 12 };
}

/** Build a 6×7 Monday-first month grid (with leading/trailing padding days). */
export function buildMonthView(year: number, month: number): MonthView {
  const firstMs = Date.UTC(year, month, 1);
  const weekday = new Date(firstMs).getUTCDay(); // 0 = Sunday
  const daysFromMonday = (weekday + 6) % 7;
  const startMs = firstMs - daysFromMonday * DAY_MS;
  const weeks: DayCell[][] = [];
  for (let w = 0; w < 6; w += 1) {
    const row: DayCell[] = [];
    for (let d = 0; d < 7; d += 1) {
      const date = new Date(startMs + (w * 7 + d) * DAY_MS);
      row.push({
        iso: utcDayKey(date),
        day: date.getUTCDate(),
        inMonth:
          date.getUTCMonth() === month && date.getUTCFullYear() === year,
      });
    }
    weeks.push(row);
  }
  const label = new Date(firstMs).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return { year, month, label, weeks };
}

/** The selectable window: `days` days back through today (UTC), inclusive. */
export function selectableRange(now: Date, days = 90): DayRange {
  const todayMs = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
  );
  return {
    maxIso: utcDayKey(new Date(todayMs)),
    minIso: utcDayKey(new Date(todayMs - (days - 1) * DAY_MS)),
  };
}

/** Whether a `YYYY-MM-DD` day is within the range (lexicographic = chronological). */
export function isSelectable(iso: string, range: DayRange): boolean {
  return iso >= range.minIso && iso <= range.maxIso;
}

/** Short UTC day label for a `YYYY-MM-DD` key, e.g. `Jun 1`. */
export function formatDayLabel(dayIso: string): string {
  return new Date(`${dayIso}T00:00:00.000Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Resolve a picked day to the uptime `anchor` instant: the day's EXCLUSIVE end
 * = next UTC midnight, clamped to `now`. The backend ends the window at the
 * anchor, so a 24h window then spans exactly `[day 00:00, nextDay 00:00)` —
 * the clean calendar day. (For "today" the clamp yields the live last-24h.)
 */
export function anchorIsoForDay(dayIso: string, now: Date): string {
  const exclusiveEnd = Date.parse(`${dayIso}T00:00:00.000Z`) + DAY_MS;
  const clamped = Math.min(exclusiveEnd, now.getTime());
  return new Date(clamped).toISOString();
}

/**
 * The day a stored anchor represents — the day containing `anchor − 1ms`.
 * Anchors are an exclusive end-of-day instant, so subtract a tick before
 * taking the day key (otherwise a midnight anchor reads as the next day).
 */
export function anchoredDayIso(anchorIso: string): string {
  return utcDayKey(new Date(Date.parse(anchorIso) - 1));
}
